import React, { Component } from 'react'
import { connect } from 'react-redux'
import { router } from 'router'
import { TweenMax, Power3, Expo } from 'gsap'

import values from 'src/values'
import Scroller from 'src/utils/scroller'
import config from 'src/config'
import raf from 'src/utils/raf'
import resize from 'src/utils/resize'
import components from 'src/utils/components'

import './styles.scss'

import IntroDesktop from './desktop/intro'
import MementoDesktop from './desktop/memento'
import ShadowDesktop from './desktop/shadow'
import TriangleDesktop from './desktop/triangle'
import ColumnDesktop from './desktop/column'
import GridDesktop from './desktop/grid'
import PerpectiveDesktop from './desktop/perspective'
import StaircaseDesktop from './desktop/staircase'
import VideoDesktop from './desktop/video'
import ColumnVideoDesktop from './desktop/columnVideo'

import IntroMobile from './mobile/intro'
import MementoMobile from './mobile/memento'
import ShadowMobile from './mobile/shadow'
import TriangleMobile from './mobile/triangle'
import ColumnMobile from './mobile/column'
import GridMobile from './mobile/grid'
import PerpectiveMobile from './mobile/perspective'
import StaircaseMobile from './mobile/staircase'
import VideoMobile from './mobile/video'
import ColumnVideoMobile from './mobile/columnVideo'

import Header from './components/header'

import PlayerVideo from 'src/components/player'
import Cover from 'src/components/cover-img'

export default class World extends Component {
  constructor(props) {
    super(props)

    this.componentsDesktop = {
      intro: IntroDesktop,
      memento: MementoDesktop,
      shadow: ShadowDesktop,
      triangle: TriangleDesktop,
      column: ColumnDesktop,
      grid: GridDesktop,
      perspective: PerpectiveDesktop,
      staircase: StaircaseDesktop,
      video: VideoDesktop,
      columnVideo: ColumnVideoDesktop
    }

    this.componentsMobile = {
      intro: IntroMobile,
      memento: MementoMobile,
      shadow: ShadowMobile,
      triangle: TriangleMobile,
      column: ColumnMobile,
      grid: GridMobile,
      perspective: PerpectiveMobile,
      staircase: StaircaseMobile,
      video: VideoMobile,
      columnVideo: ColumnVideoMobile
    }

    this.key = `worldPage`

    this.coverSize = { w: 2500, h: 1405 }

    this.state = {
      components:
        values.viewport.width < config.bp.md
          ? this.componentsMobile
          : this.componentsDesktop,
      isMobile: values.viewport.width < config.bp.md
    }
  }

  load() {
    return new Promise((resolve, reject) => {
      resolve()
    })
  }

  componentDidMount() {
    raf.add(this.onRAF)
    resize.add(this.onResize, this.key)

    components.saveComponent('worldPlayer', this.refs.player)
    this.onResize()

    document.title = this.props.screens[0].title.replace('<br>', ' ')
  }

  componentDidUpdate() {
    !this.state.isMobile && this.setupScroller()
    this.onResize()
  }

  setupScroller() {
    Scroller.update({
      el: this.refs.pageContainer,
      params: {
        overflow: false,
        infinite: false
      },
      horizontal: true
    })
  }

  willAnimateIn() {
    return new Promise(resolve => {
      !this.state.isMobile && this.setupScroller()
      this.onResize()
      resolve()
    })
  }

  animateIn(previous) {
    return new Promise(resolve => {
      Scroller.updateScrollHeight()
      router.previousParam = router.ctx.params.param
      if (previous === 'home') {
        window.scrollTo(0, 0)
        TweenMax.set(this.refs.page, { autoAlpha: 1 })
        TweenMax.fromTo(
          this.refs.worldMainWrapper,
          1.2,
          { x: '30%', autoAlpha: 0 },
          {
            x: '0%',
            autoAlpha: 1,
            ease: Expo.easeOut
          }
        )
        TweenMax.fromTo(
          this.refs.blueOverlay,
          1.2,
          { x: '0%', autoAlpha: 1 },
          {
            x: '-100%',
            ease: Expo.easeOut,
            onComplete: () => {
              resolve()
            }
          }
        )
      } else {
        TweenMax.fromTo(
          this.refs.page,
          1.2,
          { x: 130, autoAlpha: 0 },
          { x: 0, autoAlpha: 1, ease: Expo.easeOut }
        )
        TweenMax.fromTo(
          this.refs.header.refs.component,
          1.2,
          { x: -130 },
          {
            x: 0,
            ease: Expo.easeOut,
            onComplete: () => {
              TweenMax.set(this.refs.page, { clearProps: 'x' })
              resolve()
            }
          }
        )
      }
    })
  }

  animateOut(next) {
    return new Promise(resolve => {
      if (next === 'home') {
        TweenMax.to(this.refs.worldMainWrapper, 0.8, {
          x: '30%',
          ease: Expo.easeIn
        })
        TweenMax.fromTo(
          this.refs.blueOverlay,
          0.8,
          { x: '-100%', autoAlpha: 1 },
          {
            x: '0%',
            ease: Expo.easeIn,
            onComplete: () => {
              TweenMax.set(this.refs.page, { autoAlpha: 0 })
              resolve()
            }
          }
        )
      } else {
        TweenMax.to(this.refs.blueOverlay, 2, {
          autoAlpha: 0,
          ease: Expo.easeIn,
          onComplete: () => {
            TweenMax.set(this.refs.page, { autoAlpha: 0 })
            resolve()
          }
        })
      }
    })
  }

  onResize = () => {
    if (!this.refs.pageContainer) return

    if (this.state.isMobile != values.viewport.width < config.bp.md) {
      this.setState({
        components:
          values.viewport.width < config.bp.md
            ? this.componentsMobile
            : this.componentsDesktop,
        isMobile: values.viewport.width < config.bp.md
      })
    }

    this.wPageContainer = this.refs.pageContainer.offsetWidth

    if (!this.refs.lineContainer) return
    const $lineContainer = this.refs.lineContainer.getBoundingClientRect()
    this.leftLineContainer = $lineContainer.left
    this.wLineContainer = this.refs.lineContainer.offsetWidth
    this.wScreen = values.scroll.width / this.props.screens.length
  }

  onRAF = () => {
    if (!this.refs.pageContainer || this.state.isMobile) return

    let translate3d

    if (this.state.isMobile) {
      translate3d = `translate3d(0, ${-values.scroll.currentY}px, 0)`
    } else {
      translate3d = `translate3d(${-values.scroll.currentX}px, 0, 0)`

      TweenMax.set(this.refs.line, {
        x: `-${100 - values.scroll.currentX / values.scroll.width * 100}%`
      })
      this.currentScreen =
        Math.ceil((values.scroll.currentX + 1) / this.wScreen) - 1

      if (this.currentScreen <= 0) {
        TweenMax.set([this.refs.nextBtn, this.refs.backBtn], {
          autoAlpha: 1
        })
        TweenMax.set(this.refs.previousBtn, {
          autoAlpha: 0
        })
      } else if (this.currentScreen >= this.props.screens.length - 1) {
        TweenMax.set(this.refs.nextBtn, {
          autoAlpha: 0
        })
      } else {
        TweenMax.set([this.refs.previousBtn, this.refs.nextBtn], {
          autoAlpha: 1
        })
        TweenMax.set(this.refs.backBtn, {
          autoAlpha: 0
        })
      }
    }

    this.refs.pageContainer.style.transform = translate3d
    this.refs.pageContainer.style.webkitTransform = translate3d
    this.refs.pageContainer.style.mozTransform = translate3d
    this.refs.pageContainer.style.msTransform = translate3d
    this.refs.pageContainer.style.oTransform = translate3d
  }

  onClickLine = e => {
    const x =
      (e.pageX - this.leftLineContainer) /
      this.wLineContainer *
      values.scroll.width

    Scroller.scrollTo(x, 0.3).then()
  }

  onClickPrevious = () => {
    this.currentScreen - 1 >= 0 &&
      Scroller.scrollTo(
        this.currentScreen - 1 <= 0
          ? 0
          : this.wScreen * (this.currentScreen - 1) + this.wScreen / 2,
        0.3
      ).then()
  }

  onClickNext = () => {
    this.currentScreen + 1 <= this.props.screens.length - 1 &&
      Scroller.scrollTo(
        this.currentScreen + 1 === this.props.screens.length - 1
          ? this.wScreen * (this.currentScreen + 1) + this.wScreen
          : this.wScreen * (this.currentScreen + 1) + this.wScreen / 2,
        0.3
      ).then()
  }

  onClickBack = () => {
    const ctx = router.getContext()

    router.goto(`/${ctx.locale}`)
  }

  render() {
    const ctx = router.getContext()

    return (
      <div
        ref="page"
        className={`page world-page ${
          this.state.isMobile ? 'm-display' : 'd-display'
        } page-${ctx.locale}`}>
        <Header ref="header" />
        <div ref="worldMainWrapper" className="world-main-wrapper">
          <div
            ref="pageContainer"
            className="main-content"
            style={{
              width:
                !this.state.isMobile &&
                values.viewport.width * this.props.screens.length,
              ...(this.state.isMobile &&
                this.props.mobile &&
                this.props.mobile.mainContentStyle)
            }}>
            {this.props.screens.map((screen, i) => {
              const Component = this.state.components[screen.type]
              return (
                <Component
                  item={i}
                  ref={`screen${i}`}
                  key={`screen${i}`}
                  coverSize={this.coverSize}
                  {...screen}
                  specificModule={
                    this.props.screens.length - 1 === i || i === 0
                  }
                />
              )
            })}
          </div>

          {!this.state.isMobile && (
            <div onClick={this.onClickLine} className="progress-line-wrapper">
              <span ref="lineContainer" className="line-container-progress">
                <span ref="line" className="line" />
              </span>
            </div>
          )}
          {!this.state.isMobile && (
            <p
              className="btn world-title"
              dangerouslySetInnerHTML={{ __html: this.props.screens[0].title }}
            />
          )}

          {!this.state.isMobile && (
            <div
              onClick={this.onClickNext}
              ref="nextBtn"
              className="container-btn container-btn-next">
              <p className="btn">
                <span className="arrow ">
                  <span className="middle" />
                  <span className="top" />
                  <span className="btm" />
                </span>
                <span className="title-btn">{this.props.nextPage}</span>
              </p>
            </div>
          )}
          {!this.state.isMobile && (
            <div
              onClick={this.onClickPrevious}
              ref="previousBtn"
              className="container-btn container-btn-previous">
              <p className="btn">
                <span className="arrow ">
                  <span className="middle" />
                  <span className="top" />
                  <span className="btm" />
                </span>
                <span className="title-btn">{this.props.previousPage}</span>
              </p>
            </div>
          )}
          <div
            onClick={this.onClickBack}
            ref="backBtn"
            className="container-btn container-btn-back">
            <p className="btn">
              <span className="arrow ">
                <span className="middle" />
                <span className="top" />
                <span className="btm" />
              </span>
              <span className="title-btn">{this.props.backWorld}</span>
            </p>
          </div>
        </div>
        <span ref="blueOverlay" className="overlay-world in-out" />
        <PlayerVideo ref="player" idYt={this.props.screens[0].idYt} />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/pages/World/index.js